import PermissionButton from '../../permissions/PermissionButton';
import React from 'react';
import { Button, Modal, Form, Input, Space, message } from 'antd';
import { useCreateSurplus, useUnvalidateSurplus } from '../../hooks/regHooks';
import { PlusCircleOutlined, DeleteOutlined } from '@ant-design/icons';

const SurplusButtons = ({ reglement, onSurplusChange }) => {
  const [form] = Form.useForm();
  const { handleCreate, isCreating } = useCreateSurplus();
  const { handleUnvalidate, isUnvalidating } = useUnvalidateSurplus();

  const showCreateSurplusModal = () => {
    if (!reglement.remainingAmount || reglement.remainingAmount <= 0) {
      message.error('Aucun montant restant disponible pour la création de surplus');
      return;
    }

    Modal.confirm({
      title: 'Créer un Surplus',
      icon: <PlusCircleOutlined />,
      content: (
        <Form form={form} layout="vertical" initialValues={{ MNT_SURPLUS: reglement.remainingAmount }}>
          <Form.Item
            name="MNT_SURPLUS"
            label="Montant du Surplus"
            rules={[
              { required: true, message: 'Veuillez entrer le montant du surplus' },
              {
                validator: (_, value) => {
                  if (value > reglement.remainingAmount) {
                    return Promise.reject('Le montant ne peut pas dépasser le solde restant');
                  }
                  if (value <= 0) {
                    return Promise.reject('Le montant doit être supérieur à 0');
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input 
              type="number" 
              prefix="MAD" 
              max={reglement.remainingAmount}
              step="0.01"
            />
          </Form.Item>
          <Form.Item name="REMARQUE" label="Remarques">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      ),
      okText: 'Créer',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          const values = await form.validateFields();
          const surplusData = {
            ...values,
            CODE_CLT: reglement.CODE_CLT,
            REF_REGV: reglement.REF_REGV,
          };

          await handleCreate(surplusData);
          message.success('Surplus créé avec succès');
          if (onSurplusChange) {
            onSurplusChange();
          }
        } catch (error) {
          message.error(`Échec de la création du surplus: ${error.message}`);
        }
      },
      width: 500,
    });
  };

  const showUnvalidateSurplusModal = () => {
    // First check if there's an associated surplus
    const hasSurplus = reglement.reglementdetails.some(
      detail => detail.REF_AV_FAC?.startsWith('SUR')
    );

    if (!hasSurplus) {
      message.warning('Aucun surplus trouvé pour ce règlement');
      return;
    }

    Modal.confirm({
      title: 'Annuler le Surplus',
      icon: <DeleteOutlined />,
      content: 'Êtes-vous sûr de vouloir annuler ce surplus ? Cela supprimera le montant du surplus et ajustera le solde du client.',
      okText: 'Annuler le Surplus',
      okType: 'danger',
      cancelText: 'Fermer',
      onOk: async () => {
        try {
          // Find the surplus reference from reglement details
          const surplusDetail = reglement.reglementdetails.find(
            detail => detail.REF_AV_FAC?.startsWith('SUR')
          );
          
          await handleUnvalidate(surplusDetail.REF_AV_FAC);
          message.success('Surplus annulé avec succès');
          if (onSurplusChange) {
            onSurplusChange();
          }
        } catch (error) {
          message.error(`Échec de l'annulation du surplus: ${error.message}`);
        }
      },
    });
  };

  return (
    <Space>
      <PermissionButton permissionModule="payments" permissionAction="surplus"
        type="primary"
        icon={<PlusCircleOutlined />}
        onClick={showCreateSurplusModal}
        loading={isCreating}
        disabled={!reglement.remainingAmount || reglement.remainingAmount <= 0}
      >
        Créer un Surplus
      </PermissionButton>
      <PermissionButton permissionModule="payments" permissionAction="reverseSurplus"
        type="default"
        danger
        icon={<DeleteOutlined />}
        onClick={showUnvalidateSurplusModal}
        loading={isUnvalidating}
      >
        Annuler le Surplus
      </PermissionButton>
    </Space>
  );
};

export default SurplusButtons;